<template>
  <tab-layout :full-width="true">
    <div slot="tabContent" class="bg-white rounded mb-4 p-3 p-sm-4">
      <div class="d-flex flex-wrap justify-content-between align-items-start mb-3">
        <page-heading>Merge Horse</page-heading>
        <app-button variant="outline-primary" :block="false" @click="goBack">Back to Horse</app-button>
      </div>

      <div class="alert alert-danger">
        <strong>This operation cannot be undone.</strong>
        Horse #{{ sourceHorseId }} will be removed. Its runs, results and related records will be moved to the horse selected below.
      </div>

      <form novalidate @submit.prevent="handlePreview">
        <form-alert :message="error" />
        <div class="f-form">
          <div class="f-form__half">
            <f-search-select
              id="merge-target-horse"
              v-model="targetHorseId"
              label="Horse to keep"
              :options="targetOptions"
              :loading="searchLoading"
              :required="true"
              :show-reset="false"
              placeholder="Search by horse name..."
              hint="The selected horse's profile details will be kept."
              @search-change="handleSearch"
              @select="handleTargetSelect"
            />
          </div>
        </div>
        <app-button
          class="mt-2"
          :block="false"
          type="submit"
          :loading="previewLoading"
          :disabled="!targetHorseId"
        >Review Merge</app-button>
      </form>

      <template v-if="preview">
        <div class="row mt-4">
          <div class="col-12 col-lg-6 mb-3 mb-lg-0">
            <section class="merge-horse__card merge-horse__card--remove rounded p-3 h-100">
              <h2 class="h4">Horse to remove</h2>
              <horse-merge-profile :horse="preview.source" />
            </section>
          </div>
          <div class="col-12 col-lg-6">
            <section class="merge-horse__card merge-horse__card--keep rounded p-3 h-100">
              <h2 class="h4">Horse to keep</h2>
              <horse-merge-profile :horse="preview.target" />
            </section>
          </div>
        </div>

        <section class="mt-4">
          <h2 class="h4">Records to transfer</h2>
          <div class="merge-horse__counts">
            <div v-for="item in transferCounts" :key="item.key" class="bg-lighter rounded p-3">
              <span class="text-muted d-block">{{ item.label }}</span>
              <strong class="h4 mb-0">{{ item.value }}</strong>
            </div>
          </div>
        </section>

        <div v-if="preview.warnings.length" class="alert alert-warning mt-4 mb-0">
          <strong>Review these differences:</strong>
          <ul class="mb-0 mt-2">
            <li v-for="warning in preview.warnings" :key="warning">{{ warning }}</li>
          </ul>
        </div>

        <div v-if="preview.blockers.length" class="alert alert-danger mt-4 mb-0">
          <strong>This merge is blocked:</strong>
          <ul class="mb-0 mt-2">
            <li v-for="blocker in preview.blockers" :key="blocker">{{ blocker }}</li>
          </ul>
        </div>

        <form v-if="preview.can_merge" class="mt-4" novalidate @submit.prevent="handleMerge">
          <div class="f-form">
            <div class="f-form__full">
              <f-textarea
                id="merge-reason"
                v-model="reason"
                label="Reason for merge"
                :rows="3"
                :required="true"
                placeholder="Explain why these records are duplicates..."
              />
            </div>
            <div class="f-form__full">
              <f-checkbox
                id="confirm-horse-merge"
                v-model="confirmed"
                label="I have reviewed both horses and understand that this operation cannot be undone."
                :required="true"
              />
            </div>
          </div>
          <app-button
            variant="primary"
            :block="false"
            type="submit"
            :loading="merging"
            :disabled="!canSubmit"
          >Merge Horses</app-button>
        </form>
      </template>

      <modal v-if="showConfirmModal && preview" size="lg" @close="closeConfirmModal">
        <h3 slot="header" class="modal-title">Please confirm the horses to be merged</h3>
        <div slot="body">
          <div class="alert alert-warning">
            <strong>This operation cannot be undone.</strong>
            Horse #{{ preview.source.id }} will be removed and its related records will be transferred to horse #{{ preview.target.id }}.
          </div>
          <div class="row align-items-start">
            <div class="col-12 col-md-6 mb-3 mb-md-0">
              <h4>Horse To Keep</h4>
              <div class="bg-lighter rounded p-3">
                <horse-merge-profile :horse="preview.target" />
              </div>
            </div>
            <div class="col-12 col-md-6">
              <h4>Horse To Remove</h4>
              <div class="bg-lighter rounded p-3">
                <horse-merge-profile :horse="preview.source" />
              </div>
            </div>
          </div>
        </div>
        <div slot="footer" class="w-100 d-flex justify-content-end">
          <app-button
            :block="false"
            :loading="merging"
            :disabled="merging"
            @click="confirmMerge"
          >Confirm</app-button>
          <app-button
            class="ml-2"
            variant="outline-primary"
            :block="false"
            :disabled="merging"
            @click="closeConfirmModal"
          >Cancel</app-button>
        </div>
      </modal>
    </div>
  </tab-layout>
</template>

<script>
import {
  defineComponent, ref, computed, watch, onMounted, onBeforeUnmount, getCurrentInstance
} from '@vue/composition-api'
import TabLayout from '@/components/TabLayout'
import PageHeading from '@/components/PageHeading'
import AppButton from '@/components/AppButton'
import Modal from '@/components/Modal'
import FormAlert from '@/components/FormAlert'
import FSearchSelect from '@/components/FormFields/FSearchSelect'
import FTextarea from '@/components/FormFields/FTextarea'
import FCheckbox from '@/components/FormFields/FCheckbox'
import HorseMergeProfile from '@/components/Horses/HorseMergeProfile'
import EventBus from '@/store/EventBus'
import useHorseMerge from '@/composables/horses/useHorseMerge'

const COUNT_LABELS = {
  nominations: 'Nominations',
  draws: 'Runs',
  results: 'Results',
  prizes: 'Prizes',
  horse_points: 'Point records',
  horse_states: 'Status history',
  rookies: 'Rookie nominations',
  rookie_memberships: 'Rookie memberships',
  owners: 'Owners',
  riders: 'Riders',
  awards: 'Awards',
  sire_eligibilities: 'Sire eligibilities',
  offspring: 'Offspring'
}

function extractError(err) {
  const data = err.response != null ? err.response.data : null
  return data != null && data.message != null
    ? data.message
    : 'A server error occurred. Please try again later.'
}

export default defineComponent({
  name: 'HorseMerge',
  components: {
    TabLayout,
    PageHeading,
    AppButton,
    Modal,
    FormAlert,
    FSearchSelect,
    FTextarea,
    FCheckbox,
    HorseMergeProfile
  },
  props: {
    sourceHorseId: { type: [String, Number], required: true }
  },
  setup(props) {
    const { proxy } = getCurrentInstance()
    const horseMerge = useHorseMerge(proxy)
    const targetHorseId = ref(null)
    const selectedTarget = ref(null)
    const reason = ref('')
    const confirmed = ref(false)
    const error = ref(null)
    const showConfirmModal = ref(false)

    const targetOptions = computed(() => {
      const selected = horseMerge.preview.value != null
        ? horseMerge.preview.value.target
        : selectedTarget.value
      if (!selected) return horseMerge.searchResults.value
      const hasSelected = horseMerge.searchResults.value.some(horse => horse.id === selected.id)
      return hasSelected ? horseMerge.searchResults.value : [selected, ...horseMerge.searchResults.value]
    })
    const transferCounts = computed(() => {
      if (!horseMerge.preview.value) return []
      return Object.keys(COUNT_LABELS).map(key => ({
        key,
        label: COUNT_LABELS[key],
        value: horseMerge.preview.value.transfer_counts[key]
      }))
    })
    const canSubmit = computed(() => (
      horseMerge.preview.value != null
      && horseMerge.preview.value.can_merge
      && reason.value.trim().length > 0
      && confirmed.value
    ))

    watch(targetHorseId, () => {
      error.value = null
      reason.value = ''
      confirmed.value = false
      showConfirmModal.value = false
      horseMerge.clearPreview()
    })

    function handleSearch(query) {
      horseMerge.search(query, props.sourceHorseId)
    }

    function handleTargetSelect(target) {
      selectedTarget.value = target
    }

    async function handlePreview() {
      if (!targetHorseId.value) return
      error.value = null
      try {
        await horseMerge.loadPreview(props.sourceHorseId, targetHorseId.value)
      } catch (err) {
        error.value = extractError(err)
      }
    }

    function handleMerge() {
      if (!canSubmit.value) return
      error.value = null
      showConfirmModal.value = true
    }

    async function confirmMerge() {
      if (!canSubmit.value || horseMerge.merging.value) return
      error.value = null
      try {
        const response = await horseMerge.merge(
          props.sourceHorseId,
          targetHorseId.value,
          horseMerge.preview.value.preview_token,
          reason.value.trim()
        )
        showConfirmModal.value = false
        proxy.$router.push({ name: 'Admin Horse Profile', params: { slug: response.data.target_horse_id } }, () => {
          proxy.$nextTick(() => EventBus.$emit('show-success', response.message))
        })
      } catch (err) {
        showConfirmModal.value = false
        error.value = extractError(err)
      }
    }

    function closeConfirmModal() {
      if (!horseMerge.merging.value) showConfirmModal.value = false
    }

    function goBack() {
      proxy.$router.push({ name: 'Admin Horse Profile', params: { slug: props.sourceHorseId } })
    }

    onMounted(() => {
      horseMerge.loadInitialOptions(props.sourceHorseId).catch(() => {})
    })
    onBeforeUnmount(() => {
      horseMerge.reset()
    })

    return {
      targetHorseId,
      targetOptions,
      reason,
      confirmed,
      error,
      showConfirmModal,
      transferCounts,
      canSubmit,
      preview: horseMerge.preview,
      searchLoading: horseMerge.searchLoading,
      previewLoading: horseMerge.previewLoading,
      merging: horseMerge.merging,
      handleSearch,
      handleTargetSelect,
      handlePreview,
      handleMerge,
      confirmMerge,
      closeConfirmModal,
      goBack
    }
  }
})
</script>

<style scoped>
.merge-horse__card {
  border: 2px solid transparent;
}

.merge-horse__card--remove {
  border-color: var(--danger);
}

.merge-horse__card--keep {
  border-color: var(--success);
}

.merge-horse__counts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.75rem;
}
</style>



// WEBPACK FOOTER //
// src/views/Horses/Merge/HorseMerge.vue